import { RemoteError, type AccessLevel, type Op, type Remote, type RemoteOpMeta } from './types';

/**
 * Microsoft OneDrive storage through Microsoft Graph.
 *
 * Folder layout (one business = one folder, in the owner's OneDrive, shared with the others):
 *   Nandanam Farms/
 *     business.json          marks the folder as a Nandanam business (written last when created or moved)
 *     ops/<opId>.json        one file per change, created once and never modified
 *     history/*.json         ops carried over by "Move business", with their original server times
 *     documents/ receipts/ evidence/   uploaded files
 *     moved.json             present only in an old folder after the business moved elsewhere
 *
 * Only `fetch` and an access token are needed, so this runs the same in the app and in tests.
 */

export const GRAPH = 'https://graph.microsoft.com/v1.0';
export const BUSINESS_FOLDER = 'Nandanam Farms';
const SIMPLE_UPLOAD_MAX = 4 * 1024 * 1024;
const CHUNK = 320 * 1024 * 10; // upload sessions need multiples of 320 KiB

/** Thrown by the token getter when the person has to sign in again. */
export class SignInRequired extends Error {
  constructor(message = 'Sign-in expired') { super(message); this.name = 'SignInRequired'; }
}

export interface GraphOptions {
  getToken: () => Promise<string>;
  fetch?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
}

export interface DriveItem {
  id: string;
  name: string;
  size?: number;
  eTag?: string;
  createdDateTime?: string;
  folder?: { childCount?: number };
  file?: { mimeType?: string };
  parentReference?: { driveId?: string; id?: string; path?: string };
  remoteItem?: DriveItem;
  shared?: { owner?: { user?: { displayName?: string; email?: string } } };
  createdBy?: { user?: { displayName?: string; email?: string } };
  '@microsoft.graph.downloadUrl'?: string;
}

export interface BusinessLocation { driveId: string; folderId: string }

/** Thin Graph client: adds the token, retries throttling, and turns failures into RemoteError. */
export class Graph {
  private f: typeof fetch;
  private sleep: (ms: number) => Promise<void>;
  constructor(private o: GraphOptions) {
    this.f = o.fetch ?? ((...a) => fetch(...a));
    this.sleep = o.sleep ?? (ms => new Promise(r => setTimeout(r, ms)));
  }

  /** Authenticated request to a Graph path (or an absolute nextLink). Returns the Response for 2xx only. */
  async req(method: string, path: string, init: { body?: BodyInit; json?: unknown; headers?: Record<string, string>; ok?: number[] } = {}): Promise<Response> {
    let token: string;
    try { token = await this.o.getToken(); } catch (e) {
      if (e instanceof RemoteError) throw e;
      throw new RemoteError('auth-expired', e instanceof SignInRequired ? e.message : 'Sign-in expired — sign in again');
    }
    const headers: Record<string, string> = { Authorization: `Bearer ${token}`, ...init.headers };
    let body = init.body;
    if (init.json !== undefined) { body = JSON.stringify(init.json); headers['Content-Type'] = 'application/json'; }
    const url = path.startsWith('https://') ? path : GRAPH + path;
    return this.send(url, { method, headers, body }, init.ok);
  }

  /** Request to a pre-authenticated URL (download links, upload sessions): no token is sent. */
  async raw(url: string, init: RequestInit = {}, ok?: number[]): Promise<Response> { return this.send(url, init, ok); }

  async json<T>(method: string, path: string, json?: unknown): Promise<T> {
    const r = await this.req(method, path, json === undefined ? {} : { json });
    return (await r.json()) as T;
  }

  /** All pages of a collection. */
  async all<T>(path: string): Promise<T[]> {
    const out: T[] = [];
    let next: string | undefined = path;
    while (next) {
      const page: { value: T[]; '@odata.nextLink'?: string } = await this.json('GET', next);
      out.push(...page.value);
      next = page['@odata.nextLink'];
    }
    return out;
  }

  private async send(url: string, init: RequestInit, ok: number[] = []): Promise<Response> {
    for (let attempt = 0; ; attempt++) {
      let r: Response;
      try { r = await this.f(url, init); } catch (e) {
        throw new RemoteError('offline', `No connection to ${new URL(url).host} (${e instanceof Error ? e.message : String(e)})`);
      }
      if (r.ok || ok.includes(r.status)) return r;
      if ((r.status === 429 || r.status === 503 || r.status === 504) && attempt < 3) {
        const after = Number(r.headers.get('Retry-After'));
        await this.sleep(Math.min(10_000, (Number.isFinite(after) && after > 0 ? after * 1000 : 1000 * 2 ** attempt)));
        continue;
      }
      throw await toRemoteError(r);
    }
  }
}

async function toRemoteError(r: Response): Promise<RemoteError> {
  let code = '', message = '';
  try { const b = await r.json(); code = b?.error?.code ?? ''; message = b?.error?.message ?? ''; } catch { /* not JSON */ }
  const err = new RemoteError(
    r.status === 401 ? 'auth-expired'
      : r.status === 403 ? 'denied'
      : r.status === 404 ? 'not-found'
      : r.status === 507 || code === 'quotaLimitReached' || code === 'insufficientStorage' ? 'quota'
      : 'unknown',
    r.status === 401 ? 'Sign-in expired — sign in again'
      : r.status === 403 ? 'No permission for this in the business folder'
      : r.status === 404 ? 'Not found in OneDrive'
      : r.status === 507 || code === 'quotaLimitReached' ? 'OneDrive storage is full'
      : `OneDrive error ${r.status}${message ? `: ${message}` : ''}`,
  );
  (err as any).status = r.status;
  (err as any).code = code;
  return err;
}

/** Graph address of an item, optionally a path below it (`/drives/d/items/i:/a/b.json:`). */
export function itemPath(loc: { driveId: string; itemId: string }, rel?: string): string {
  const base = `/drives/${encodeURIComponent(loc.driveId)}/items/${encodeURIComponent(loc.itemId)}`;
  if (!rel) return base;
  return `${base}:/${rel.split('/').map(encodeURIComponent).join('/')}:`;
}
const at = (loc: BusinessLocation, rel?: string) => itemPath({ driveId: loc.driveId, itemId: loc.folderId }, rel);

/** OneDrive returns seconds or milliseconds depending on the account; ordering needs one format. */
export const normalizeTime = (t: string) => new Date(t).toISOString();

const OP_FILE = /^([0-9A-Za-z_-]+)\.json$/;

export interface MovedInfo { movedAt: string; movedBy: string; to: string }
interface HistoryFile { format: 'nandanam-history'; v: 1; movedAt: string; movedBy: string; from: BusinessLocation; ops: { op: Op; createdTime: string }[]; fileMap: Record<string, string> }

/**
 * `Remote` over a OneDrive business folder. Ops in history/ (from a move) keep the server times they
 * had in the old folder, so the order, and with it every booking number, stays exactly as it was.
 */
export class OneDriveRemote implements Remote {
  /** Set when the folder has moved.json: the business continues in another account's OneDrive. */
  movedTo: MovedInfo | null = null;
  /** Old file id → new file id for uploads carried over by a move. */
  fileMap: Record<string, string> = {};
  private viewOnly = false;
  private role: AccessLevel | null = null;
  private opsFolderId: string | null = null;
  private historyFolderId: string | null = null;
  private downloadUrls = new Map<string, string>();
  private history = new Map<string, { key: string; ops: Map<string, { op: Op; createdTime: string }> }>();

  constructor(private g: Graph, readonly loc: BusinessLocation, private o: { owner: boolean; myUserId?: string }) {}

  get isOwner() { return this.o.owner; }

  async access(): Promise<AccessLevel> {
    let top: DriveItem[];
    try { top = await this.g.all<DriveItem>(`${at(this.loc)}/children?$select=id,name,folder,file,eTag&$top=200`); } catch (e) {
      if (e instanceof RemoteError && (e.kind === 'not-found' || e.kind === 'denied')) throw new RemoteError('denied', 'This account can no longer open the business folder');
      throw e;
    }
    this.opsFolderId = top.find(i => i.name === 'ops' && i.folder)?.id ?? null;
    this.historyFolderId = top.find(i => i.name === 'history' && i.folder)?.id ?? null;
    const moved = top.find(i => i.name === 'moved.json' && i.file);
    this.movedTo = moved ? await this.readJson<MovedInfo>(moved.id).catch(() => ({ movedAt: '', movedBy: '', to: 'another account' })) : null;
    if (this.movedTo) return 'viewer';
    if (this.o.owner) return 'editor';
    if (this.viewOnly) return 'viewer';
    if (!this.role) this.role = await this.sharedRole();
    return this.role;
  }

  /** Best effort: OneDrive does not always tell a recipient their own role. Unknown = try as editor. */
  private async sharedRole(): Promise<AccessLevel> {
    try {
      const perms = await this.g.all<any>(`${at(this.loc)}/permissions`);
      const mine = perms.filter(p => {
        const u = p.grantedToV2?.user ?? p.grantedTo?.user;
        return u?.id && this.o.myUserId && String(u.id).toLowerCase() === this.o.myUserId.toLowerCase();
      });
      if (mine.some(p => (p.roles ?? []).some((r: string) => r === 'write' || r === 'owner'))) return 'editor';
      if (mine.length && mine.every(p => (p.roles ?? []).every((r: string) => r === 'read'))) return 'viewer';
    } catch { /* not visible to recipients on every account type */ }
    return 'editor';
  }

  async listOps(): Promise<RemoteOpMeta[]> {
    if (!this.opsFolderId && !this.historyFolderId) await this.access();
    const out = new Map<string, RemoteOpMeta>();
    if (this.historyFolderId) {
      const files = (await this.g.all<DriveItem>(`${itemPath({ driveId: this.loc.driveId, itemId: this.historyFolderId })}/children?$top=200`)).filter(f => f.file && f.name.endsWith('.json'));
      for (const f of files.sort((a, b) => (a.name < b.name ? -1 : 1))) {
        const key = f.eTag ?? f.id;
        let h = this.history.get(f.id);
        if (!h || h.key !== key) {
          const body = await this.readJson<HistoryFile>(f.id, f['@microsoft.graph.downloadUrl']);
          if (body?.format !== 'nandanam-history') continue;
          h = { key, ops: new Map(body.ops.map(x => [x.op.opId, { op: x.op, createdTime: normalizeTime(x.createdTime) }])) };
          this.history.set(f.id, h);
          Object.assign(this.fileMap, body.fileMap ?? {});
        }
        for (const [opId, x] of h.ops) out.set(opId, { opId, fileId: `history:${f.id}`, createdTime: x.createdTime });
      }
    }
    if (this.opsFolderId) {
      const files = await this.g.all<DriveItem>(`${itemPath({ driveId: this.loc.driveId, itemId: this.opsFolderId })}/children?$top=1000`);
      for (const f of files) {
        const m = f.file && OP_FILE.exec(f.name);
        if (!m || !f.createdDateTime || out.has(m[1]!)) continue;
        if (f['@microsoft.graph.downloadUrl']) this.downloadUrls.set(f.id, f['@microsoft.graph.downloadUrl']);
        out.set(m[1]!, { opId: m[1]!, fileId: f.id, createdTime: normalizeTime(f.createdDateTime) });
      }
    }
    return [...out.values()];
  }

  async readOp(meta: RemoteOpMeta): Promise<Op> {
    if (meta.fileId.startsWith('history:')) {
      const x = this.history.get(meta.fileId.slice(8))?.ops.get(meta.opId);
      if (!x) throw new RemoteError('not-found', 'Moved history file changed while reading — sync again');
      return x.op;
    }
    const op = await this.readJson<Op>(meta.fileId, this.downloadUrls.get(meta.fileId));
    if (op?.opId !== meta.opId) throw new RemoteError('unknown', `Change file ${meta.opId}.json in OneDrive is damaged`);
    return op;
  }

  async createOp(op: Op): Promise<RemoteOpMeta> {
    if (this.movedTo) throw new RemoteError('denied', 'This business has moved to another account — changes are no longer saved here');
    const path = `${at(this.loc, `ops/${op.opId}.json`)}/content?@microsoft.graph.conflictBehavior=fail`;
    let item: DriveItem;
    try {
      const r = await this.g.req('PUT', path, { body: JSON.stringify(op), headers: { 'Content-Type': 'application/json' }, ok: [409] });
      item = r.status === 409
        // An earlier attempt was saved but its answer was lost: use that file, never a second one.
        ? await this.g.json<DriveItem>('GET', `${at(this.loc, `ops/${op.opId}.json`)}?$select=id,name,createdDateTime`)
        : await r.json();
    } catch (e) {
      if (e instanceof RemoteError && e.kind === 'denied') {
        this.viewOnly = true;
        throw new RemoteError('denied', 'View-only access — the owner has shared the folder with you as a viewer');
      }
      throw e;
    }
    return { opId: op.opId, fileId: item.id, createdTime: normalizeTime(item.createdDateTime!) };
  }

  private async readJson<T>(fileId: string, downloadUrl?: string): Promise<T> {
    return readItemJson<T>(this.g, { driveId: this.loc.driveId, itemId: fileId }, downloadUrl);
  }
}

async function readItemJson<T>(g: Graph, item: { driveId: string; itemId: string }, downloadUrl?: string): Promise<T> {
  if (downloadUrl) {
    try { return (await (await g.raw(downloadUrl)).json()) as T; } catch (e) {
      if (e instanceof RemoteError && e.kind === 'offline') throw e;
      // Download links expire after about an hour; fall back to an authenticated read.
    }
  }
  const r = await g.req('GET', `${itemPath(item)}/content`);
  try { return (await r.json()) as T; } catch { throw new RemoteError('unknown', 'A file in the business folder is not readable'); }
}

/* ---------------- files ---------------- */

/** Upload a file to `rel` under the folder. With conflict "fail", an existing file is returned instead. */
export async function uploadFile(g: Graph, loc: BusinessLocation, rel: string, data: Blob, conflict: 'fail' | 'replace' = 'fail'): Promise<DriveItem> {
  const target = at(loc, rel);
  if (data.size <= SIMPLE_UPLOAD_MAX) {
    const r = await g.req('PUT', `${target}/content?@microsoft.graph.conflictBehavior=${conflict}`, { body: data, headers: { 'Content-Type': data.type || 'application/octet-stream' }, ok: [409] });
    if (r.status === 409) return g.json<DriveItem>('GET', target);
    return r.json();
  }
  const s = await g.req('POST', `${target}/createUploadSession`, { json: { item: { '@microsoft.graph.conflictBehavior': conflict } }, ok: [409] });
  if (s.status === 409) return g.json<DriveItem>('GET', target);
  const { uploadUrl } = await s.json() as { uploadUrl: string };
  let last: Response | null = null;
  for (let start = 0; start < data.size; start += CHUNK) {
    const end = Math.min(data.size, start + CHUNK);
    last = await g.raw(uploadUrl, { method: 'PUT', body: data.slice(start, end), headers: { 'Content-Range': `bytes ${start}-${end - 1}/${data.size}` } });
  }
  return last!.json();
}

/** A short-lived link to a file's content (null if it no longer exists). */
export async function downloadLink(g: Graph, driveId: string, itemId: string): Promise<string | null> {
  try {
    const it = await g.json<DriveItem>('GET', `${itemPath({ driveId, itemId })}?$select=id,@microsoft.graph.downloadUrl`);
    return it['@microsoft.graph.downloadUrl'] ?? null;
  } catch (e) {
    if (e instanceof RemoteError && e.kind === 'not-found') return null;
    throw e;
  }
}

/* ---------------- finding and creating the business folder ---------------- */

export interface Me { id: string; name: string; email: string; driveId: string }

export async function whoAmI(g: Graph): Promise<Me> {
  const [me, drive] = await Promise.all([
    g.json<any>('GET', '/me?$select=id,displayName,mail,userPrincipalName'),
    g.json<any>('GET', '/me/drive?$select=id'),
  ]);
  return { id: me.id, name: me.displayName || me.mail || me.userPrincipalName || 'Me', email: me.mail || me.userPrincipalName || '', driveId: drive.id };
}

export interface BusinessInfo { format: 'nandanam-business'; v: 1; name: string; createdAt: string; createdBy: string; movedFrom?: BusinessLocation & { at: string } }
export interface FoundBusiness extends BusinessLocation { name: string; sharedBy: string }

export async function readBusinessInfo(g: Graph, loc: BusinessLocation): Promise<BusinessInfo | null> {
  try {
    const b = await readItemJson<BusinessInfo>(g, { driveId: loc.driveId, itemId: (await g.json<DriveItem>('GET', at(loc, 'business.json'))).id });
    return b?.format === 'nandanam-business' ? b : null;
  } catch (e) {
    if (e instanceof RemoteError && e.kind === 'not-found') return null;
    throw e;
  }
}

/** The business folder in my own OneDrive, if there is one. */
export async function findOwnBusiness(g: Graph, me: Me): Promise<FoundBusiness | null> {
  let folder: DriveItem;
  try { folder = await g.json<DriveItem>('GET', `/me/drive/root:/${encodeURIComponent(BUSINESS_FOLDER)}`); } catch (e) {
    if (e instanceof RemoteError && e.kind === 'not-found') return null;
    throw e;
  }
  const loc = { driveId: me.driveId, folderId: folder.id };
  return (await readBusinessInfo(g, loc)) ? { ...loc, name: folder.name, sharedBy: me.name } : null;
}

/** Business folders other people have shared with me (OneDrive "Shared" list). */
export async function findSharedBusinesses(g: Graph): Promise<FoundBusiness[]> {
  const items = await g.all<DriveItem>('/me/drive/sharedWithMe');
  const out: FoundBusiness[] = [];
  for (const it of items) {
    const r = it.remoteItem ?? it;
    const driveId = r.parentReference?.driveId;
    if (!r.folder || !driveId) continue;
    const loc = { driveId, folderId: r.id };
    try { if (!(await readBusinessInfo(g, loc))) continue; } catch { continue; }
    const by = r.shared?.owner?.user ?? r.createdBy?.user;
    out.push({ ...loc, name: r.name, sharedBy: by?.displayName || by?.email || 'someone' });
  }
  return out;
}

/** Open a business folder from the sharing link the owner sent. */
export async function openShareLink(g: Graph, link: string): Promise<FoundBusiness> {
  const url = link.trim();
  if (!/^https:\/\/\S+$/.test(url)) throw new RemoteError('not-found', 'That does not look like a OneDrive link');
  const b64 = btoa(String.fromCharCode(...new TextEncoder().encode(url))).replace(/=+$/, '').replace(/\//g, '_').replace(/\+/g, '-');
  let it: DriveItem;
  try { it = await g.json<DriveItem>('GET', `/shares/u!${b64}/driveItem`); } catch (e) {
    if (e instanceof RemoteError && (e.kind === 'not-found' || e.kind === 'denied')) throw new RemoteError('not-found', 'This link does not open a folder for your Microsoft account. Ask the owner to share it with your email address.');
    throw e;
  }
  const r = it.remoteItem ?? it;
  const driveId = r.parentReference?.driveId;
  if (!r.folder || !driveId) throw new RemoteError('not-found', 'This link is for a file, not the business folder');
  const loc = { driveId, folderId: r.id };
  if (!(await readBusinessInfo(g, loc))) throw new RemoteError('not-found', 'This folder is not a Nandanam Farms business folder');
  const by = r.shared?.owner?.user ?? r.createdBy?.user;
  return { ...loc, name: r.name, sharedBy: by?.displayName || by?.email || 'someone' };
}

/** Create the folder skeleton in my OneDrive (or reuse an unfinished one). business.json is not written here. */
async function makeFolder(g: Graph, me: Me): Promise<BusinessLocation & { complete: boolean }> {
  let folder: DriveItem;
  const r = await g.req('POST', '/me/drive/root/children', { json: { name: BUSINESS_FOLDER, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' }, ok: [409] });
  if (r.status === 409) {
    folder = await g.json<DriveItem>('GET', `/me/drive/root:/${encodeURIComponent(BUSINESS_FOLDER)}`);
    if (!folder.folder) throw new RemoteError('unknown', `There is a file called "${BUSINESS_FOLDER}" in your OneDrive. Rename it, then try again.`);
  } else folder = await r.json();
  const loc = { driveId: me.driveId, folderId: folder.id };
  const complete = !!(await readBusinessInfo(g, loc));
  if (!complete) {
    const kids = await g.all<DriveItem>(`${at(loc)}/children?$select=name&$top=200`);
    const ours = new Set(['ops', 'history', 'documents', 'receipts', 'evidence']);
    const strange = kids.filter(k => !ours.has(k.name));
    if (strange.length) throw new RemoteError('unknown', `Your OneDrive already has a "${BUSINESS_FOLDER}" folder with other files in it (${strange.slice(0, 3).map(k => k.name).join(', ')}). Rename that folder, then try again.`);
    for (const name of ['ops', 'documents', 'receipts', 'evidence']) {
      if (kids.some(k => k.name === name)) continue;
      await g.req('POST', `${at(loc)}/children`, { json: { name, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' }, ok: [409] });
    }
  }
  return { ...loc, complete };
}

/** Start a new business folder in my OneDrive. Reuses an existing business folder instead of making a second. */
export async function createBusiness(g: Graph, me: Me, now = new Date()): Promise<FoundBusiness> {
  const f = await makeFolder(g, me);
  if (!f.complete) {
    const info: BusinessInfo = { format: 'nandanam-business', v: 1, name: BUSINESS_FOLDER, createdAt: now.toISOString(), createdBy: me.email || me.name };
    await uploadFile(g, f, 'business.json', new Blob([JSON.stringify(info, null, 1)], { type: 'application/json' }), 'fail');
  }
  return { driveId: f.driveId, folderId: f.folderId, name: BUSINESS_FOLDER, sharedBy: me.name };
}

/* ---------------- moving the business to another account ---------------- */

export interface MoveProgress { step: 'folder' | 'files' | 'history' | 'check' | 'finish'; done: number; total: number }
export interface MoveResult { dest: FoundBusiness; ops: number; files: number; oldFolderMarked: boolean }

const FILE_DIRS = ['documents', 'receipts', 'evidence'];

async function walk(g: Graph, driveId: string, itemId: string, rel: string, out: { item: DriveItem; rel: string }[]) {
  let kids: DriveItem[];
  try { kids = await g.all<DriveItem>(`${itemPath({ driveId, itemId })}/children?$top=200`); } catch (e) {
    if (e instanceof RemoteError && e.kind === 'not-found') return;
    throw e;
  }
  for (const k of kids) {
    if (k.folder) await walk(g, driveId, k.id, `${rel}/${k.name}`, out);
    else if (k.file) out.push({ item: k, rel: `${rel}/${k.name}` });
  }
}

/**
 * Copy a business that someone shared with me into my own OneDrive, then mark the old folder as moved.
 * `log` must be every op from the old folder with its server time (sync first; nothing may be waiting).
 * Safe to run again after an interruption: finished files are reused and business.json is written last.
 */
export async function moveBusiness(g: Graph, me: Me, from: OneDriveRemote, log: { op: Op; createdTime: string | null }[], onProgress: (p: MoveProgress) => void = () => {}, now = new Date()): Promise<MoveResult> {
  if (from.loc.driveId.toLowerCase() === me.driveId.toLowerCase()) throw new RemoteError('unknown', 'This business is already in your own OneDrive');
  if (log.some(x => !x.createdTime)) throw new RemoteError('unknown', 'Some changes on this device are not in OneDrive yet. Sync first, then move.');
  onProgress({ step: 'folder', done: 0, total: 1 });
  const dest = await makeFolder(g, me);
  if (dest.complete) throw new RemoteError('unknown', `Your OneDrive already has a "${BUSINESS_FOLDER}" business folder. Moving would make a second copy, so nothing was changed.`);

  // Uploaded documents: copy through this device, keeping their folder paths.
  const files: { item: DriveItem; rel: string }[] = [];
  for (const dir of FILE_DIRS) {
    const top = await g.json<DriveItem>('GET', at(from.loc, dir)).catch(e => { if (e instanceof RemoteError && e.kind === 'not-found') return null; throw e; });
    if (top) await walk(g, from.loc.driveId, top.id, dir, files);
  }
  const fileMap: Record<string, string> = { ...from.fileMap };
  for (let i = 0; i < files.length; i++) {
    onProgress({ step: 'files', done: i, total: files.length });
    const { item, rel } = files[i]!;
    const link = item['@microsoft.graph.downloadUrl'] ?? (await downloadLink(g, from.loc.driveId, item.id));
    if (!link) continue;
    const blob = await (await g.raw(link)).blob();
    const copy = await uploadFile(g, dest, rel, new Blob([blob], { type: item.file?.mimeType || blob.type }), 'fail');
    fileMap[item.id] = copy.id;
    for (const [old, cur] of Object.entries(fileMap)) if (cur === item.id) fileMap[old] = copy.id;
  }
  onProgress({ step: 'files', done: files.length, total: files.length });

  // Every op with its original server time, so replay order and booking numbers stay the same.
  onProgress({ step: 'history', done: 0, total: 1 });
  const history: HistoryFile = {
    format: 'nandanam-history', v: 1, movedAt: now.toISOString(), movedBy: me.email || me.name, from: from.loc,
    ops: log.map(x => ({ op: x.op, createdTime: x.createdTime! })), fileMap,
  };
  await uploadFile(g, dest, 'history/0001.json', new Blob([JSON.stringify(history)], { type: 'application/json' }), 'replace');

  // Read it back the way the app will before calling the move done.
  onProgress({ step: 'check', done: 0, total: 1 });
  const check = new OneDriveRemote(g, dest, { owner: true });
  await check.access();
  const metas = await check.listOps();
  const want = new Map(log.map(x => [x.op.opId, normalizeTime(x.createdTime!)]));
  if (metas.length !== want.size || metas.some(m => want.get(m.opId) !== m.createdTime)) {
    throw new RemoteError('unknown', `The copy in your OneDrive does not match (${metas.length} of ${want.size} changes). Nothing else was changed; try again.`);
  }

  onProgress({ step: 'finish', done: 0, total: 1 });
  const info: BusinessInfo = { format: 'nandanam-business', v: 1, name: BUSINESS_FOLDER, createdAt: now.toISOString(), createdBy: me.email || me.name, movedFrom: { ...from.loc, at: now.toISOString() } };
  await uploadFile(g, dest, 'business.json', new Blob([JSON.stringify(info, null, 1)], { type: 'application/json' }), 'replace');

  let oldFolderMarked = false;
  try {
    const moved: MovedInfo = { movedAt: now.toISOString(), movedBy: me.email || me.name, to: me.email || me.name };
    await uploadFile(g, from.loc, 'moved.json', new Blob([JSON.stringify(moved, null, 1)], { type: 'application/json' }), 'replace');
    oldFolderMarked = true;
  } catch (e) {
    if (!(e instanceof RemoteError && e.kind === 'denied')) throw e;
  }
  return { dest: { driveId: dest.driveId, folderId: dest.folderId, name: BUSINESS_FOLDER, sharedBy: me.name }, ops: log.length, files: files.length, oldFolderMarked };
}
